import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Mail, 
  Eye, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Search,
  Filter,
  Plus,
  XCircle
} from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { StatusBadge } from '@/components/common/StatusBadge';
import { sanitizeEmailHTML } from '@/utils/htmlSanitizer';
import { Helmet } from 'react-helmet-async';
import { Progress } from '@/components/ui/progress';
import EmailViewer from '@/components/email/EmailViewer';

// Interface para os emails transacionais
interface TransactionalEmail {
  id: string;
  email_template_id: string;
  recipient_email: string;
  subject: string;
  body: string;
  state: 'pending' | 'sent' | 'failed';
  sent_at: string | null;
  error_message: string | null;
  entity: string;
  entity_id: string | null;
  created_at: string;
  email_template?: {
    name: string;
  };
}

// Interface para os templates de email
interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  is_active: boolean;
}

// Interface para os filtros
interface Filters {
  state: string; // 'all', 'sent', 'pending', 'failed'
  search: string;
  template: string; // 'all' ou ID do template
  days: number; // últimos X dias, 0 = todos
}

const TransactionalEmails = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState<TransactionalEmail[]>([]);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [currentEmail, setCurrentEmail] = useState<TransactionalEmail | null>(null);
  const [filters, setFilters] = useState<Filters>({
    state: 'all',
    search: '',
    template: 'all',
    days: 30
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    sent: 0,
    failed: 0
  });
  const [resending, setResending] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setPage(0);
    fetchEmails(true);
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEmails(true),
        fetchTemplates(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados de emails transacionais');
    } finally {
      setLoading(false);
    }
  };

  // Buscar emails transacionais
  const fetchEmails = async (reset: boolean = false) => {
    try {
      const currentPage = reset ? 0 : page;
      const from = currentPage * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('transactional_emails')
        .select(`
          *,
          email_template:email_template_id(name)
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      // Aplicar filtro de dias
      if (filters.days > 0) {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - filters.days);
        
        query = query.gte('created_at', daysAgo.toISOString());
      }

      // Aplicar filtros de estado
      if (filters.state && filters.state !== 'all') {
        query = query.eq('state', filters.state);
      }

      // Aplicar filtros de template
      if (filters.template && filters.template !== 'all') {
        query = query.eq('email_template_id', filters.template);
      }

      // Aplicar filtros de busca
      if (filters.search) {
        query = query.or(`recipient_email.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`);
      }

      // Paginação
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      
      if (reset) {
        setEmails(data || []);
      } else {
        setEmails(prev => [...prev, ...(data || [])]);
      }

      // Verificar se há mais emails para carregar
      if (count) {
        setHasMoreEmails(from + (data?.length || 0) < count);
      }
    } catch (error: any) {
      console.error('Erro ao buscar emails:', error);
      toast.error('Erro ao carregar emails transacionais', {
        description: error.message
      });
    }
  };

  // Carregar mais emails
  const loadMoreEmails = async () => {
    setPage(prev => prev + 1);
    await fetchEmails(false);
  };

  // Buscar templates de email
  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Erro ao buscar templates:', error);
      toast.error('Erro ao carregar templates de email', {
        description: error.message
      });
    }
  };

  // Buscar estatísticas
  const fetchStats = async () => {
    try {
      // Filtro de data para estatísticas
      let dateFilter = {};
      if (filters.days > 0) {
        const daysAgo = new Date();
        daysAgo.setDate(daysAgo.getDate() - filters.days);
        dateFilter = { created_at: `gte.${daysAgo.toISOString()}` };
      }

      // Total de emails
      const { count: total, error: totalError } = await supabase
        .from('transactional_emails')
        .select('*', { count: 'exact', head: true })
        .match(dateFilter);

      if (totalError) throw totalError;

      // Emails pendentes
      const { count: pending, error: pendingError } = await supabase
        .from('transactional_emails')
        .select('*', { count: 'exact', head: true })
        .match({ state: 'pending', ...dateFilter });

      if (pendingError) throw pendingError;

      // Emails enviados
      const { count: sent, error: sentError } = await supabase
        .from('transactional_emails')
        .select('*', { count: 'exact', head: true })
        .match({ state: 'sent', ...dateFilter });

      if (sentError) throw sentError;

      // Emails com falha
      const { count: failed, error: failedError } = await supabase
        .from('transactional_emails')
        .select('*', { count: 'exact', head: true })
        .match({ state: 'failed', ...dateFilter });

      if (failedError) throw failedError;

      setStats({
        total: total || 0,
        pending: pending || 0,
        sent: sent || 0,
        failed: failed || 0
      });
    } catch (error: any) {
      console.error('Erro ao buscar estatísticas:', error);
    }
  };

  // Abrir diálogo para visualizar email
  const openViewDialog = (email: TransactionalEmail) => {
    setCurrentEmail(email);
    setIsViewDialogOpen(true);
  };

  // Reenviar email com falha
  const handleResendEmail = async (emailId: string) => {
    setResending(true);
    try {
      const { error } = await supabase
        .from('transactional_emails')
        .update({ 
          state: 'pending',
          error_message: null
        })
        .eq('id', emailId);

      if (error) throw error;
      
      toast.success('Email marcado para reenvio');
      fetchData();
    } catch (error: any) {
      console.error('Erro ao reenviar email:', error);
      toast.error('Erro ao reenviar email', {
        description: error.message
      });
    } finally {
      setResending(false);
    }
  };

  // Formatar data e hora
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  };

  // Limpar todos os filtros
  const clearFilters = () => {
    setFilters({
      state: 'all',
      search: '',
      template: 'all',
      days: 30
    });
  };

  // Obter texto para período de dias
  const getDaysText = (days: number) => {
    if (days === 0) return 'Todo o período';
    if (days === 1) return 'Último dia';
    if (days === 7) return 'Últimos 7 dias';
    if (days === 30) return 'Últimos 30 dias';
    if (days === 60) return 'Últimos 60 dias';
    if (days === 90) return 'Últimos 90 dias';
    return `Últimos ${days} dias`;
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Emails Transacionais</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Hero Header with Background Image */}
        <div 
          className="relative overflow-hidden" 
          style={{ 
            backgroundColor: '#1F1746',
            backgroundImage: 'url(/elemento-01.svg)',
            backgroundSize: '200%',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Emails Transacionais</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Monitore e gerencie todos os emails enviados automaticamente pelo sistema
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default"
                  onClick={() => navigate('/templates-email')} 
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <Mail className="h-4 w-4 mr-2" /> 
                  Ver Templates
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Total Emails Card */}
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        <Mail className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Emails</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.total}</div>
                  </div>
                  <p className="text-xs text-gray-500">{getDaysText(filters.days)}</p>
                </CardContent>
              </Card>

              {/* Sent Emails Card */}
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Enviados</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.sent}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.sent / stats.total) * 100 : 0} className="h-1.5" />
                  <div className="text-xs text-gray-500 mt-2">
                    {stats.total > 0 ? `${Math.round((stats.sent / stats.total) * 100)}% do total` : '0%'}
                  </div>
                </CardContent>
              </Card>

              {/* Pending Emails Card */}
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-yellow-100 rounded-md">
                        <Clock className="h-4 w-4 text-yellow-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Pendentes</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.pending}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.pending / stats.total) * 100 : 0} className="h-1.5" />
                  <div className="text-xs text-gray-500 mt-2">
                    {stats.total > 0 ? `${Math.round((stats.pending / stats.total) * 100)}% do total` : '0%'}
                  </div>
                </CardContent>
              </Card>

              {/* Failed Emails Card */}
              <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-red-100 rounded-md">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Falhas</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.failed}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.failed / stats.total) * 100 : 0} className="h-1.5" />
                   <div className="text-xs text-gray-500 mt-2">
                    {stats.total > 0 ? `${Math.round((stats.failed / stats.total) * 100)}% do total` : '0%'}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Card */}
            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Buscar por email ou assunto..."
                      className="pl-10 h-10 bg-white"
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    />
                  </div>
                  
                  {/* Status Filter */}
                  <Select value={filters.state} onValueChange={(value) => setFilters(prev => ({ ...prev, state: value }))}>
                    <SelectTrigger className="w-full md:w-[180px] h-10 bg-white">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      <SelectItem value="sent">Enviados</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                      <SelectItem value="failed">Falhas</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Template Filter */}
                  <Select value={filters.template} onValueChange={(value) => setFilters(prev => ({ ...prev, template: value }))}>
                    <SelectTrigger className="w-full md:w-[180px] h-10 bg-white">
                      <SelectValue placeholder="Template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Templates</SelectItem>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Days Filter */}
                  <Select value={filters.days.toString()} onValueChange={(value) => setFilters(prev => ({ ...prev, days: parseInt(value) }))}>
                    <SelectTrigger className="w-full md:w-[180px] h-10 bg-white">
                      <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Todo o período</SelectItem>
                      <SelectItem value="1">Último dia</SelectItem>
                      <SelectItem value="7">Últimos 7 dias</SelectItem>
                      <SelectItem value="30">Últimos 30 dias</SelectItem>
                      <SelectItem value="60">Últimos 60 dias</SelectItem>
                      <SelectItem value="90">Últimos 90 dias</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Button variant="ghost" onClick={clearFilters}>Limpar</Button>
                </div>

                {loading && emails.length === 0 ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center space-x-4">
                        <Skeleton className="h-12 w-full" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Destinatário</TableHead>
                          <TableHead>Assunto</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emails.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="h-24 text-center">
                              <div className="flex flex-col items-center justify-center text-muted-foreground">
                                <XCircle className="h-8 w-8 mb-2" />
                                <p>Nenhum email encontrado</p>
                                <p className="text-sm mt-1">Tente ajustar os filtros ou envie novos emails</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          emails.map((email) => (
                            <TableRow key={email.id}>
                              <TableCell className="max-w-[180px] truncate" title={email.recipient_email}>
                                {email.recipient_email}
                              </TableCell>
                              <TableCell className="max-w-[200px] truncate" title={email.subject}>
                                {email.subject}
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={email.state} context="email" />
                              </TableCell>
                              <TableCell>
                                {email.state === 'sent' 
                                  ? formatDateTime(email.sent_at) 
                                  : formatDateTime(email.created_at)}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openViewDialog(email)}
                                    title="Visualizar"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {email.state === 'failed' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleResendEmail(email.id)}
                                      disabled={resending}
                                      title="Tentar reenviar"
                                      className="text-amber-600 hover:text-amber-700"
                                    >
                                      <RefreshCw className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>

                    {hasMoreEmails && (
                      <div className="mt-4 text-center">
                        <Button 
                          variant="outline"
                          onClick={loadMoreEmails}
                          disabled={loading}
                          className="mx-auto"
                        >
                          Carregar mais
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            
            <div className="h-16" /> {/* Bottom spacing */}
          </div>
        </div>
      
      {/* Diálogo para visualizar email */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Visualização do Email</DialogTitle>
            <DialogDescription>
              {currentEmail?.email_template?.name || 'Email Transacional'}
            </DialogDescription>
          </DialogHeader>

          {currentEmail && (
            <div className="space-y-4">
              {/* Informações do email */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <p className="text-sm font-extrabold mb-1">Destinatário:</p>
                  <p className="text-sm">{currentEmail.recipient_email}</p>
                </div>
                <div>
                  <p className="text-sm font-extrabold mb-1">Status:</p>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={currentEmail.state} context="email" />
                    {currentEmail.state === 'sent' && currentEmail.sent_at && (
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(currentEmail.sent_at)}
                      </span>
                    )}
                  </div>
                  {currentEmail.state === 'failed' && currentEmail.error_message && (
                    <p className="text-xs text-red-500 mt-1 break-words">
                      Erro: {currentEmail.error_message}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-sm font-extrabold mb-1">Data de Criação:</p>
                  <p className="text-sm">{formatDateTime(currentEmail.created_at)}</p>
                </div>
              </div>

              {/* Visualização do email como o destinatário recebeu */}
              <div className="max-h-[60vh] overflow-auto">
                <EmailViewer
                  htmlContent={sanitizeEmailHTML(currentEmail.body)}
                  subject={currentEmail.subject}
                  recipientEmail={currentEmail.recipient_email}
                />
              </div>

              {/* Ações */}
              {currentEmail.state === 'failed' && (
                <div className="flex justify-end pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleResendEmail(currentEmail.id)}
                    disabled={resending}
                    className="gap-2"
                  >
                    <RefreshCw className={`h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
                    {resending ? 'Reenviando...' : 'Tentar Reenviar'}
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransactionalEmails; 